import React from 'react';
import { ArrowRight } from 'lucide-react';
import type { IntakePersonProgress, OrbitOverview } from '../../../types/aai';
import { CountUp } from '../../../components/aa';
import { CoverageRadar, FactsDonut, MappedRing, cardsOf, useGrow } from '../../orbit-room/OverviewCharts';
import { completion, completionWord } from '../../orbit-room/phases';
import { Card, CardLink, plural } from './primitives';
import '../../orbit-room/room.css';

/* What Orbit understands about the company, as three cards on the dashboard
   grid. The charts are the Room's own, so the two never disagree. */

export const MappedCard: React.FC<{ room: OrbitOverview; onAnswer: () => void }> = ({ room, onAnswer }) => {
  const pct = completion(room);
  const thin = room.thin || [];
  let line = 'Every area has enough to work with.';
  if (thin.length > 0 && thin.length === room.areas.length) line = 'Every area still needs answers before Orbit can rely on it.';
  if (thin.length > 0 && thin.length < room.areas.length) line = `Orbit still needs more on ${thin.map((a) => a.label).join(', ')}.`;
  return (
    <Card title="Company map" sub={`Average of the ${room.areas.length} areas Orbit tracks`}>
      <div className="oroom">
        <div className="ov-ring">
          <MappedRing pct={pct} />
          <div className="lbl">
            <strong>{completionWord(pct, thin.length)}</strong>
            <small>{thin.length > 0 ? `${thin.length} of ${room.areas.length} areas still thin` : 'No area is thin'}</small>
          </div>
        </div>
      </div>
      <p className="m-0 mt-4 line-clamp-4 text-[12.5px] leading-[19px] text-ink-muted">
        {room.sentence && <span className="text-ink">{room.sentence} </span>}
        {line}
      </p>
      <div className="mt-auto pt-4">
        <CardLink onClick={onAnswer}>Answer your cards <ArrowRight size={12} /></CardLink>
      </div>
    </Card>
  );
};

export const CoverageCard: React.FC<{ room: OrbitOverview }> = ({ room }) => (
  <Card title="Coverage by area" sub="How much Orbit knows in each area. Hover one for its cards">
    {room.areas.length > 0 && <div className="oroom flex-1"><CoverageRadar areas={room.areas} /></div>}
    {room.areas.length === 0 && <p className="m-0 text-[12.5px] text-ink-dim">Nothing read yet. Give Orbit a website or a document.</p>}
  </Card>
);

export const KnowledgeCard: React.FC<{ room: OrbitOverview; onAnswer: () => void }> = ({ room, onAnswer }) => {
  const cards = cardsOf(room);
  return (
    <Card title="What Orbit knows" sub="Every card in the areas, by where it stands">
      <div className="oroom flex flex-1 items-center">
        <FactsDonut known={cards.known} confirm={cards.confirm} open={cards.open} onAnswer={onAnswer} />
      </div>
    </Card>
  );
};

/* Intake progress is per person: what they answered of everything their role
   is asked. `null` while it loads; missing when the reader may not see it. */
export const TeamCard: React.FC<{ room: OrbitOverview; intake?: Record<string, IntakePersonProgress> | null; onOpen: () => void }> = ({ room, intake, onOpen }) => {
  const t = useGrow(1);
  const { people, onboarded, departments } = room.counts;
  const progress = intake ? Object.values(intake) : [];
  const started = progress.filter((p) => p.answered > 0).length;
  const complete = progress.filter((p) => p.total > 0 && p.answered >= p.total).length;
  const answered = progress.reduce((sum, p) => sum + p.answered, 0);
  const asked = progress.reduce((sum, p) => sum + p.total, 0);
  const rows = [
    { label: 'Onboarded', hint: 'Joined the workspace', value: onboarded, tone: '#18191D' },
  ];
  if (intake) {
    rows.push({ label: 'Intake started', hint: 'Answered at least one intake question', value: Math.min(started, people), tone: '#92949D' });
    rows.push({ label: 'Intake complete', hint: 'Answered every question their role is asked', value: Math.min(complete, people), tone: '#303238' });
  }
  let footLine = 'Everyone added is onboarded';
  if (people === 0) footLine = 'Nobody added yet';
  if (people - onboarded > 0) footLine = `${plural(people - onboarded, 'person', 'people')} still to onboard`;
  if (intake && asked > 0) footLine = `${footLine} · ${answered} of ${asked} intake questions answered so far`;
  return (
    <Card title="Team" sub="Who is in, and how far their intake is" action={<CardLink onClick={onOpen}>People <ArrowRight size={12} /></CardLink>}>
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-line bg-line">
        {[{ label: 'People', value: people }, { label: 'Departments', value: departments }].map((k) => (
          <div key={k.label} className="bg-white px-3 py-2.5">
            <b className="block text-[18px] font-semibold leading-tight tabular-nums text-ink"><CountUp value={k.value} /></b>
            <span className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-dim">{k.label}</span>
          </div>
        ))}
      </div>
      <div className="mt-4 grid gap-3.5">
        {rows.map((r) => {
          const share = people > 0 ? r.value / people : 0;
          return (
            <div key={r.label}>
              <div className="mb-1.5 flex items-baseline justify-between gap-2">
                <span className="min-w-0">
                  <span className="text-[12.5px] font-medium text-ink">{r.label}</span>
                  <span className="ml-1.5 text-[11px] text-ink-soft">{r.hint}</span>
                </span>
                <span className="shrink-0 text-[12px] tabular-nums text-ink">
                  <b className="font-semibold">{r.value}</b>
                  <span className="text-ink-soft"> of {people}</span>
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-canvas-inset" role="img" aria-label={`${r.label}: ${r.value} of ${people}`}>
                <div className="h-full rounded-full" style={{ width: `${share * 100 * t}%`, background: r.tone }} />
              </div>
            </div>
          );
        })}
        {intake === null && <div className="h-8 animate-pulse rounded-lg bg-canvas-inset" aria-busy="true" />}
      </div>
      <p className="m-0 mt-auto pt-4 text-[11.5px] text-ink-soft">{footLine}</p>
    </Card>
  );
};
